import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import { useEffect, useState } from "react";
import { Autocomplete, CircularProgress } from "@mui/material";
import { SignupFormResp } from "../data/useSignupForm";
import { useBarnehageFylkeData } from "../data/useBarnehageFylkeData";
import { useBarnehageData } from "../data/useBarnehageData";
import { StepValidateDetails } from "../data/useSignupFormValidator";
import { WhiteTextField } from "../styles/styledTextField";

export interface FinnBarnehageFormProps {
  signupdata: SignupFormResp;
  stepValidation: StepValidateDetails;
}

export default function FinnBarnehageForm(props: FinnBarnehageFormProps) {
  const [selectedFylke, setSelectedFylke] = useState<withLabel<IFylke> | null>(
    props.signupdata.entityData.fylke
      ? {
          label: props.signupdata.entityData.fylke,
          fylke: props.signupdata.entityData.fylke,
          fylkesnummer: props.signupdata.entityData.fylkesnummer,
        }
      : null,
  );

  const [selectedKommune, setSelectedKommune] =
    useState<withLabel<IKommune> | null>(
      props.signupdata.entityData.fylke && props.signupdata.entityData.kommune
        ? {
            label: props.signupdata.entityData.kommune,
            fylke: props.signupdata.entityData.fylke,
            fylkesnummer: props.signupdata.entityData.fylkesnummer,
            kommune: props.signupdata.entityData.kommune,
            kommunenummer: props.signupdata.entityData.kommunenummer,
          }
        : null,
    );

  const [selectedBarnehage, setSelectedBarnehage] =
    useState<withLabel<IBarnehage> | null>(
      props.signupdata.entityData.navn
        ? {
            label: props.signupdata.entityData.navn,
            navn: props.signupdata.entityData.navn,
            orgnr: props.signupdata.entityData.orgnr,
            koordinatLatLng: [],
            fylke: props.signupdata.entityData.fylke,
            fylkesnummer: props.signupdata.entityData.fylkesnummer,
            kommune: props.signupdata.entityData.kommune,
            kommunenummer: props.signupdata.entityData.kommunenummer,
            alder: "",
            eierform: "",
            antallBarn: 0,
          }
        : null,
    );

  const { visibleBarnehager } = useBarnehageData({
    selectedFylke,
    selectedKommune,
  });
  const { visibleFylker, visibleKommuner, loading } = useBarnehageFylkeData();

  useEffect(() => {
    props.signupdata.patchEntityData({
      navn: selectedBarnehage?.navn ?? "",
      orgnr: selectedBarnehage?.orgnr ?? "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBarnehage]);

  useEffect(() => {
    props.signupdata.patchEntityData({
      fylke: selectedFylke?.fylke ?? "",
      fylkesnummer: selectedFylke?.fylkesnummer ?? "",
      kommune: selectedKommune?.kommune ?? "",
      kommunenummer: selectedKommune?.kommunenummer ?? "",
    });

    if (!selectedKommune) setSelectedBarnehage(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFylke, selectedKommune]);

  useEffect(() => {
    if (!selectedFylke) setSelectedKommune(null);
  }, [selectedFylke]);

  return (
    <>
      {loading && (
        <center>
          <CircularProgress />
        </center>
      )}

      {!loading && (
        <>
          <Typography component="h2" variant="h2">
            Finn din barnehage
          </Typography>
          <Grid container spacing={3} sx={{ marginTop: 0 }}>
            <Grid item xs={12}>
              <Autocomplete
                autoFocus
                disablePortal
                value={selectedFylke}
                id="fylke-selector"
                options={visibleFylker}
                onChange={(e: any, newValue: withLabel<IFylke> | null) => {
                  setSelectedFylke(newValue);
                  if (newValue) {
                    const kommuner = visibleKommuner.filter(
                      (k) => k.fylkesnummer === newValue?.fylkesnummer,
                    );
                    if (kommuner.length === 1) {
                      setSelectedKommune(kommuner[0]);
                    }
                  }
                }}
                renderInput={(params) => (
                  <WhiteTextField
                    {...params}
                    autoFocus
                    label="Fylke"
                    variant="filled"
                    error={
                      props.stepValidation.dirtyStep &&
                      props.stepValidation.validStepDetails["fylkesnummer"]
                        ? true
                        : false
                    }
                    helperText={
                      props.stepValidation.dirtyStep
                        ? props.stepValidation.validStepDetails["fylkesnummer"]
                        : null
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                disablePortal
                disabled={selectedFylke ? false : true}
                value={selectedKommune}
                id="kommune-selector"
                options={visibleKommuner.filter(
                  (k) => k.fylkesnummer === selectedFylke?.fylkesnummer,
                )}
                renderInput={(params) => (
                  <WhiteTextField
                    {...params}
                    label="Kommune"
                    variant="filled"
                    error={
                      props.stepValidation.dirtyStep &&
                      props.stepValidation.validStepDetails["kommunenummer"]
                        ? true
                        : false
                    }
                    helperText={
                      props.stepValidation.dirtyStep
                        ? props.stepValidation.validStepDetails["kommunenummer"]
                        : null
                    }
                  />
                )}
                onChange={(e: any, newValue: withLabel<IKommune> | null) =>
                  setSelectedKommune(newValue)
                }
              />
            </Grid>

            {!props.signupdata.customOrganization && (
              <Grid item xs={12}>
                <Autocomplete
                  disablePortal
                  id="barnehage-selector"
                  disabled={selectedKommune ? false : true}
                  value={selectedBarnehage}
                  options={visibleBarnehager}
                  renderInput={(params) => (
                    <WhiteTextField
                      {...params}
                      label="Barnehage"
                      variant="filled"
                      error={
                        props.stepValidation.dirtyStep &&
                        props.stepValidation.validStepDetails["entitydropdown"]
                          ? true
                          : false
                      }
                      helperText={
                        props.stepValidation.dirtyStep
                          ? props.stepValidation.validStepDetails[
                              "entitydropdown"
                            ]
                          : null
                      }
                    />
                  )}
                  onChange={(e: any, newValue: withLabel<IBarnehage> | null) =>
                    setSelectedBarnehage(newValue)
                  }
                />
              </Grid>
            )}
          </Grid>
        </>
      )}
    </>
  );
}
